<template>
  <div class="articles-list" :class="{ 'articles-list--loading': loading }">
    <ul v-if="articles.length" class="page-articles-list__grid">
      <li
        v-for="article in articles"
        :key="article.url"
        class="page-articles-list__item"
      >
        <article class="article-card">
          <div class="article-card__image">
            <img
              v-if="article.imageUrl"
              :src="article.imageUrl"
              :alt="article.title"
              class="article-card__img"
              loading="lazy"
              width="392"
              height="240"
            />
          </div>
          <div class="article-card__body">
            <time class="article-card__date">{{ article.date }}</time>
            <div class="article-card__text">
              <h3 class="article-card__title">{{ article.title }}</h3>
              <p class="article-card__summary">{{ article.summary }}</p>
            </div>
            <a :href="article.url" class="article-card__link">{{ readMoreLabel }}</a>
          </div>
        </article>
      </li>
    </ul>

    <nav
      v-if="pageInfo.totalPages > 1"
      class="article-pagination"
      :aria-label="paginationLabel"
    >
      <ul class="article-pagination__list">
        <li
          v-if="pageInfo.currentPage > 1"
          class="article-pagination__prev"
        >
          <a
            :href="getPageUrl(pageInfo.currentPage - 1)"
            class="article-pagination__btn"
            :aria-label="getPageLabel(pageInfo.currentPage - 1)"
            rel="prev"
            @click.prevent="goToPage(pageInfo.currentPage - 1)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 6L9 12L15 18"
                stroke="#05140E"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
        </li>

        <li v-for="item in paginationItems" :key="item.key">
          <span
            v-if="item.dots"
            class="article-pagination__btn article-pagination__btn_dots"
            aria-hidden="true"
          >…</span>
          <a
            v-else
            :href="getPageUrl(item.page)"
            class="article-pagination__btn"
            :class="{ 'article-pagination__btn_active': item.page === pageInfo.currentPage }"
            :aria-label="getPageLabel(item.page)"
            :aria-current="item.page === pageInfo.currentPage ? 'page' : null"
            @click.prevent="handlePageClick(item.page)"
          >{{ item.label }}</a>
        </li>

        <li
          v-if="pageInfo.currentPage < pageInfo.totalPages"
          class="article-pagination__next"
        >
          <a
            :href="getPageUrl(pageInfo.currentPage + 1)"
            class="article-pagination__btn"
            :aria-label="getPageLabel(pageInfo.currentPage + 1)"
            rel="next"
            @click.prevent="goToPage(pageInfo.currentPage + 1)"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 6L15 12L9 18"
                stroke="#05140E"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ArticlesList',

  props: {
    initialArticles: {
      type: Array,
      required: true,
    },
    initialPageInfo: {
      type: Object,
      required: true,
    },
    baseUrl: {
      type: String,
      required: true,
    },
    readMoreLabel: {
      type: String,
      default: 'Lees meer',
    },
    paginationLabel: {
      type: String,
      default: 'Paginering',
    },
    pageLabel: {
      type: String,
      default: 'Pagina {page}',
    },
  },

  data() {
    return {
      articles: this.initialArticles,
      pageInfo: { ...this.initialPageInfo },
      loading: false,
      popstateHandler: null,
    };
  },

  computed: {
    paginationItems() {
      const { currentPage, totalPages } = this.pageInfo;

      if (totalPages <= 1) return [];

      // Show all pages if they fit in 5
      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => ({
          page: i + 1,
          label: String(i + 1),
          key: `page-${i + 1}`,
        }));
      }

      // Always exactly 5 items: page numbers + dots
      if (currentPage <= 3) {
        // Near start: 1 2 3 … last
        return [
          { page: 1, label: '1', key: 'page-1' },
          { page: 2, label: '2', key: 'page-2' },
          { page: 3, label: '3', key: 'page-3' },
          { dots: true, key: 'dots-right' },
          { page: totalPages, label: String(totalPages), key: `page-${totalPages}` },
        ];
      }

      if (currentPage >= totalPages - 2) {
        // Near end: 1 … last-2 last-1 last
        return [
          { page: 1, label: '1', key: 'page-1' },
          { dots: true, key: 'dots-left' },
          { page: totalPages - 2, label: String(totalPages - 2), key: `page-${totalPages - 2}` },
          { page: totalPages - 1, label: String(totalPages - 1), key: `page-${totalPages - 1}` },
          { page: totalPages, label: String(totalPages), key: `page-${totalPages}` },
        ];
      }

      // Middle: 1 … current … last
      return [
        { page: 1, label: '1', key: 'page-1' },
        { dots: true, key: 'dots-left' },
        { page: currentPage, label: String(currentPage), key: `page-${currentPage}` },
        { dots: true, key: 'dots-right' },
        { page: totalPages, label: String(totalPages), key: `page-${totalPages}` },
      ];
    },
  },

  methods: {
    getPageLabel(page) {
      return this.pageLabel.replace('{page}', page);
    },

    getPageUrl(page) {
      return page === 1 ? this.baseUrl : `${this.baseUrl}/p${page}`;
    },

    handlePageClick(page) {
      if (page !== this.pageInfo.currentPage) {
        this.goToPage(page);
      }
    },

    async fetchPage(page) {
      if (this.loading) return false;

      this.loading = true;

      try {
        const url = this.getPageUrl(page);
        const { data } = await axios.get(`${url}?format=json`);

        if (!data.articles) {
          window.location.href = url;
          return false;
        }

        this.articles = data.articles;
        this.pageInfo = data.pageInfo;

        window.scrollTo({ top: this.$el.getBoundingClientRect().top + window.pageYOffset, behavior: 'smooth' });
        return true;
      } catch {
        window.location.href = this.getPageUrl(page);
        return false;
      } finally {
        this.loading = false;
      }
    },

    async goToPage(page) {
      const success = await this.fetchPage(page);
      if (success) {
        window.history.pushState(null, '', this.getPageUrl(page));
      }
    },
  },

  mounted() {
    // Remove server-rendered HTML — Vue takes over rendering
    document.getElementById('js-articles-server')?.remove();

    // Handle browser back/forward by fetching the page from the URL
    this.popstateHandler = () => {
      const match = window.location.pathname.match(/\/p(\d+)$/);
      const page = match ? parseInt(match[1], 10) : 1;
      this.fetchPage(page);
    };
    window.addEventListener('popstate', this.popstateHandler);
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.popstateHandler);
  },
};
</script>
